/**
 * data/quests/leaders — the 8 gym leaders of the Badge Circuit (main-quest.md).
 *
 * Each leader is a NEW authored regular (a pinned SimTrainer, homed at their
 * biome's node), warm and encouraging — they cheer you win OR lose. Teams are
 * Gen 1–2-focus species that EXIST in the ingested dataset, on a gentle
 * L10→L55 ramp. `gymId` matches the badge id (the defeat-gym-leader objective +
 * the battle-win event both key off it). Lines: the leader's signature quote
 * from the doc is the intro; onWin/onLose are authored warm floors in-voice.
 *
 * The renderer (GymScene / BattleScene) builds real parties from `party` via
 * society/makeInstance; this module is pure data.
 */

/** One authored gym leader + their faithful party and warm lines. */
export interface GymLeader {
  readonly gymId: string; // e.g. 'meadow-badge' (== badgeId; keyed by defeat-gym-leader)
  readonly badgeId: string; // the badge awarded (QuestState.badges)
  readonly name: string;
  readonly title: string;
  readonly biomeId: string; // matches scenes/world/biomes.ts ids
  readonly monType: string; // themed type
  readonly npcId: string; // the pinned story-NPC / quest giver id
  readonly party: readonly { readonly speciesId: string; readonly level: number }[];
  readonly intro: readonly string[]; // spoken before the battle
  readonly onWin: readonly string[]; // warm, never gloating
  readonly onLose: readonly string[]; // warm, never punishing (no-stakes wipe)
}

export const GYM_LEADERS: readonly GymLeader[] = [
  // ── Badge 1 · Sunny Meadow · Normal ──
  {
    gymId: 'meadow-badge',
    badgeId: 'meadow-badge',
    name: 'Rowan',
    title: 'the Meadowkeeper',
    biomeId: 'meadow',
    monType: 'normal',
    npcId: 'npc-leader-meadow',
    party: [
      { speciesId: 'pidgey', level: 10 },
      { speciesId: 'chansey', level: 11 },
      { speciesId: 'tauros', level: 12 },
    ],
    intro: [
      "No rush out here, friend. A gym's just a friendly measure of how well you two listen to each other.",
      'Whenever you’re ready. The meadow keeps no clock.',
    ],
    onWin: [
      'Well now, look at that. You two listen beautifully. That’s the whole of it, really.',
      'The Meadow Badge is yours, and gladly given. Come sit a while before you head on.',
    ],
    onLose: [
      'Worn out, the pair of you — no shame in that. Let’s get you home and rested.',
      'The meadow will be right here tomorrow, and so will I. Come back any time.',
    ],
  },
  // ── Badge 2 · Whispering Forest · Bug ──
  {
    gymId: 'forest-badge',
    badgeId: 'forest-badge',
    name: 'Tansy',
    title: 'who talks to beetles',
    biomeId: 'whispering-forest',
    monType: 'bug',
    npcId: 'npc-leader-forest',
    party: [
      { speciesId: 'butterfree', level: 14 },
      { speciesId: 'beedrill', level: 15 },
      { speciesId: 'scyther', level: 16 },
    ],
    intro: [
      "Shh — hear that? That's my whole team saying hello. Bugs are braver than anyone gives them credit for.",
      'Be gentle with them and they’ll be gentle with you. Mostly. Ready?',
    ],
    onWin: [
      'Oh, they LIKED you — I can tell! The Rustle Badge is yours, brave heart.',
      'Come back and say hello to the beetles some time. They’ll remember you.',
    ],
    onLose: [
      'Aw, all tuckered out — yours and mine both. Off home for a good rest, hm?',
      'No such thing as losing in the wood. Just a rematch waiting to happen.',
    ],
  },
  // ── Badge 3 · Still Lake · Water ──
  {
    gymId: 'lake-badge',
    badgeId: 'lake-badge',
    name: 'Marlow',
    title: 'of the Reeds',
    biomeId: 'still-lake',
    monType: 'water',
    npcId: 'npc-leader-lake',
    party: [
      { speciesId: 'psyduck', level: 18 },
      { speciesId: 'seaking', level: 19 },
      { speciesId: 'lapras', level: 20 },
    ],
    intro: [
      "Patience is the whole sport, really. Cast, wait, and let the lake decide. Your team's got that patient look — let's see it.",
      'Easy now. The water rewards the calm ones.',
    ],
    onWin: [
      'Steady as still water, the lot of you. The Ripple Badge — you’ve earned every ring of it.',
      'Come fish with me at dawn some day. Best quiet in the region.',
    ],
    onLose: [
      'Tide’s against you today, is all. Let’s get everyone dry and rested.',
      'The lake’ll be just as still tomorrow. So will my kettle. Come back.',
    ],
  },
  // ── Badge 4 · Quiet Cavern · Rock/Ground ──
  {
    gymId: 'cavern-badge',
    badgeId: 'cavern-badge',
    name: 'Bruno Cobble',
    title: 'the lamplighter',
    biomeId: 'quiet-cavern',
    monType: 'rock',
    npcId: 'npc-leader-cavern',
    party: [
      { speciesId: 'graveler', level: 24 },
      { speciesId: 'onix', level: 25 },
      { speciesId: 'rhyhorn', level: 26 },
    ],
    intro: [
      "Down here you learn to trust your footing and your friends. Nothing to fear in the deep — come on, I'll leave a light on.",
      'Watch your step, and mind the echoes. Ready when you are.',
    ],
    onWin: [
      'Sure-footed and sure-hearted. The Echo Badge is yours — carry a bit of my lamplight with it.',
      'The deep’s a friendlier place with you in it. Come back and hum along some time.',
    ],
    onLose: [
      'Ah, we all stumble in the dark. Let’s get your team up top and into the sun.',
      'I’ll keep a light on for you. Come back when you’re rested — no hurry at all.',
    ],
  },
  // ── Badge 5 · Pinewood Deep · Grass ──
  {
    gymId: 'deep-forest-badge',
    badgeId: 'deep-forest-badge',
    name: 'Sage Fern',
    title: 'the old-growth warden',
    biomeId: 'deep-forest',
    monType: 'grass',
    npcId: 'npc-leader-deepforest',
    party: [
      { speciesId: 'victreebel', level: 30 },
      { speciesId: 'exeggutor', level: 31 },
      { speciesId: 'heracross', level: 32 },
    ],
    intro: [
      "The deep wood keeps its own time. So do the strongest bonds. You've been at this a while now, haven't you? It shows — in the good way.",
      'Breathe the green a moment. Then show me what’s grown between you.',
    ],
    onWin: [
      'Deep roots, the both of you. The Verdant Badge suits a bond like that.',
      'Press a flower in your notebook for me. Something to remember the quiet by.',
    ],
    onLose: [
      'Even the tallest pine bends in a hard wind. Rest now — you’ve earned it either way.',
      'The old wood is patient. So am I. Come back whenever the path brings you.',
    ],
  },
  // ── Badge 6 · Windrest Ridge · Fighting ──
  {
    gymId: 'mountain-badge',
    badgeId: 'mountain-badge',
    name: 'Kestrel',
    title: 'of the High Crags',
    biomeId: 'mountain-slope',
    monType: 'fighting',
    npcId: 'npc-leader-mountain',
    party: [
      { speciesId: 'machoke', level: 36 },
      { speciesId: 'primeape', level: 37 },
      { speciesId: 'aerodactyl', level: 38 },
    ],
    intro: [
      "Whole mountain to ourselves and the wind for a crowd! Win or lose, you'll have earned the view. Ready when you are, champ.",
      'Feel that air? That’s the good stuff. Let’s climb.',
    ],
    onWin: [
      'HA! Top of the world, you two! The Summit Badge — and the best view in the region to go with it.',
      'Come back and race me up the ridge some time. Loser buys the berries!',
    ],
    onLose: [
      'Wind won that round — happens to the best of us! Down we go for a rest, champ.',
      'The crags aren’t going anywhere, and neither’s the view. Come climb again soon.',
    ],
  },
  // ── Badge 7 · Sunken Ruins · Psychic/Ghost ──
  {
    gymId: 'ruins-badge',
    badgeId: 'ruins-badge',
    name: 'Enigma',
    title: 'keeper of the murals',
    biomeId: 'old-ruins',
    monType: 'psychic',
    npcId: 'npc-leader-ruins',
    party: [
      { speciesId: 'alakazam', level: 44 },
      { speciesId: 'gengar', level: 45 }, // doc: misdreavus→mismagius (or gengar); mismagius absent from dataset
      { speciesId: 'xatu', level: 46 },
    ],
    intro: [
      "The stones remember more than we do. They've been… restless lately. But that's a worry for after our battle. Show me what you've built.",
      'The murals are watching too, you know. Let’s give them a good story.',
    ],
    onWin: [
      'The stones approve — I felt them settle. The Relic Badge is yours, wanderer.',
      'Stay a moment. There’s… a room below I’ve been meaning to tell someone kind about.',
    ],
    onLose: [
      'The old halls tire everyone — no shame in the deep. Rest your team; the murals keep.',
      'Come back when you’re ready. The stones are patient, and so, it seems, am I.',
    ],
  },
  // ── Badge 8 · Emberfall Ashland · Fire ──
  {
    gymId: 'ashland-badge',
    badgeId: 'ashland-badge',
    name: 'Cinder Vale',
    title: 'the ashwalker',
    biomeId: 'volcanic-ashland',
    monType: 'fire',
    npcId: 'npc-leader-ashland',
    party: [
      { speciesId: 'arcanine', level: 52 },
      { speciesId: 'magmar', level: 53 },
      { speciesId: 'ninetales', level: 54 },
      { speciesId: 'charizard', level: 55 },
    ],
    intro: [
      "Last badge, and the hardest — but you didn't come all this way to be nervous now. Let's make it a battle they'll tell stories about.",
      'The shrine’s watching, and so is the whole town. Give them a warm one.',
    ],
    onWin: [
      'STORIES, I said — and that’s one they’ll tell for years! The Ember Badge, and all eight lanterns lit in your honor.',
      'You walked the whole circuit kind and came out warm. That’s the rarest thing of all. Welcome to the far side, friend.',
    ],
    onLose: [
      'The fire runs hot up here — it takes most folks a few tries. Rest now; you’re so close.',
      'Come back when you’re ready. The eighth lantern will be waiting to be lit — by you.',
    ],
  },
];

/** O(1) lookup of a leader by gymId (== badgeId). */
const LEADER_BY_GYM = new Map(GYM_LEADERS.map((l) => [l.gymId, l]));

export function gymLeaderById(gymId: string): GymLeader | undefined {
  return LEADER_BY_GYM.get(gymId);
}
